import { auth, type SchemaResponse } from "@/modules/api"
import type {
  Balance,
  BankAccount,
  BankInfoAccount,
  SubmissionCampaign,
  Transaction
} from "@/modules/types"
import type { PagingSchema } from "@/modules/types/common/schema"

export const getBankList = () => {
  return auth.get<SchemaResponse<PagingSchema<BankAccount>>>(
    "/banks/list?order=DESC&page=1&take=100&sortBy=createdAt"
  )
}

export const fetchNameByBankCode = (data: { accountNumber: string; bankCode: number }) => {
  return auth.get<SchemaResponse<{ name: string }>>(`transactions/recipient-name`, {
    params: data
  })
}

export const sendCodeVerify = () => {
  return auth.post<SchemaResponse<{ code: string }>>("/transactions/send-code", {
    type: "ADD_BANK_ACCOUNT"
  })
}

export const sendCodeVerifyWithdraw = () => {
  return auth.post<SchemaResponse<{ code: string }>>("/transactions/send-code", {
    type: "WITHDRAWAL"
  })
}

export const sendCodeVerifyDelete = () => {
  return auth.post<SchemaResponse<{ code: string }>>("/transactions/send-code", {
    type: "DELETE_BANK_ACCOUNT"
  })
}

export const createRequestBank = (data: {
  accountNumber: string
  accountHolderName: string
  bankCode: string
  code: string
}) => {
  return auth.post<SchemaResponse<BankAccount>>("/banks", data)
}

export const getBalance = () => {
  return auth.get<SchemaResponse<Balance>>("/user/bank/balance")
}
type Statistic = {
  totalEarned: number
  totalEarnPending: number
  totalWithdrawalProgressing: number
  totalWithdrawn: number
}

export const getStatistic = (prs?: { startTime?: string; endTime?: string }) => {
  const params: any = {}
  if (prs?.startTime) {
    params["startTime"] = prs.startTime
  }
  if (prs?.endTime) {
    params["endTime"] = prs.endTime
  }
  return auth.get<SchemaResponse<Statistic>>("/transactions/koc/statistic", {
    params: {
      ...params
    }
  })
}

export const getBillingCampaigns = (prs?: {
  keyword?: string
  order?: "ASC" | "DESC"
  acceptanceStatus?: string
  startTime?: string
  endTime?: string
}) => {
  const params: any = {}
  if (prs?.keyword) {
    params["search"] = prs.keyword
  }
  if (prs?.order) {
    params["order"] = prs.order
  }
  if (prs?.acceptanceStatus && prs.acceptanceStatus !== "ALL") {
    params["acceptanceStatus"] = prs.acceptanceStatus
  }
  if (prs?.startTime) {
    params["startTime"] = prs.startTime
  }
  if (prs?.endTime) {
    params["endTime"] = prs.endTime
  }
  return auth.get<SchemaResponse<PagingSchema<SubmissionCampaign>>>(
    "/kol-submissions/have-cast/list",
    {
      params: {
        ...params,
        page: 1,
        take: 20,
        sortBy: "createdAt"
      }
    }
  )
}

export const getTransactionList = (prs?: {
  keyword?: string
  order?: "ASC" | "DESC"
  paidType?: string
}) => {
  const params: any = {}
  if (prs?.keyword) {
    params["search"] = prs.keyword
  }
  if (prs?.order) {
    params["order"] = prs.order
  }
  if (prs?.paidType && prs.paidType !== "ALL") {
    params["status"] = prs.paidType
  }
  return auth.get<SchemaResponse<PagingSchema<Transaction>>>("/transactions/list", {
    params: {
      ...params,
      page: 1,
      take: 20,
      sortBy: "createdAt",
      type: "WITHDRAWAL"
    }
  })
}

export const getBankAccountList = () => {
  return auth.get<SchemaResponse<BankInfoAccount[]>>("/banks/accounts")
}

export const deleteBankAccount = (id: string, code?: string) => {
  const config = code ? { data: { code } } : {}
  return auth.delete<SchemaResponse<BankInfoAccount>>(`/banks/${id}`, config)
}

type AddBank = {
  bankCode: string
  accountHolderName: string
  accountNumber: string
  code: string
  isDefault: boolean
}

export const addBank = (data: AddBank) => {
  return auth.post<SchemaResponse<AddBank>>("/banks", data)
}

export const createRequestWithdraw = (data: {
  bankAccountId: string
  amount: number
  code: string
}) => {
  return auth.post<SchemaResponse<Transaction>>("/transactions/withdrawal", data)
}
